import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Trophy, Crown, Medal, ArrowLeft, Copy, Share2, Users, Gift, Flame,
  Sparkles, ChevronRight, Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { referralsApi } from "@/lib/api";

const BOARD_LIMIT = 25;

function Podium({ rows, selfCode }) {
  // rows are already sorted. Rearrange top-3 as [2nd, 1st, 3rd] for the podium look.
  const top = rows.slice(0, 3);
  const second = top[1];
  const first = top[0];
  const third = top[2];
  return (
    <div className="grid grid-cols-3 gap-3 sm:gap-5 items-end" data-testid="leaderboard-podium">
      <PodiumCard row={second} rank={2} selfCode={selfCode} height="h-40 sm:h-52" />
      <PodiumCard row={first} rank={1} selfCode={selfCode} height="h-48 sm:h-64" />
      <PodiumCard row={third} rank={3} selfCode={selfCode} height="h-36 sm:h-44" />
    </div>
  );
}

function PodiumCard({ row, rank, selfCode, height }) {
  const palettes = {
    1: {
      border: "border-amber-400/50",
      bg: "bg-gradient-to-b from-amber-500/20 via-amber-500/8 to-transparent",
      glow: "shadow-[0_30px_80px_-20px_rgba(251,191,36,0.45)]",
      icon: <Crown className="w-5 h-5 sm:w-6 sm:h-6" />,
      iconBg: "bg-amber-400 text-black",
      ribbon: "text-amber-300",
      label: "Champion",
    },
    2: {
      border: "border-slate-300/40",
      bg: "bg-gradient-to-b from-slate-300/15 via-slate-300/5 to-transparent",
      glow: "shadow-[0_20px_60px_-20px_rgba(203,213,225,0.3)]",
      icon: <Medal className="w-5 h-5" />,
      iconBg: "bg-slate-200 text-black",
      ribbon: "text-slate-300",
      label: "Runner-up",
    },
    3: {
      border: "border-orange-500/40",
      bg: "bg-gradient-to-b from-orange-500/15 via-orange-500/5 to-transparent",
      glow: "shadow-[0_20px_60px_-20px_rgba(249,115,22,0.35)]",
      icon: <Medal className="w-5 h-5" />,
      iconBg: "bg-orange-400 text-black",
      ribbon: "text-orange-300",
      label: "Bronze",
    },
  };
  const p = palettes[rank];
  const isSelf = row && row.referral_code === selfCode;

  if (!row) {
    return (
      <div className={`rounded-2xl border border-white/10 bg-[#0F111A]/60 ${height} grid place-items-center text-center p-3`}>
        <div>
          <div className={`font-mono text-[10px] uppercase tracking-[0.3em] ${p.ribbon}`}>#{rank}</div>
          <div className="mt-2 text-[11px] text-slate-500 font-mono leading-snug">Open slot<br/>Could be you</div>
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid={`leaderboard-podium-${rank}`}
      className={`relative rounded-2xl border ${p.border} ${p.bg} ${p.glow} ${height} px-3 py-4 sm:p-4 flex flex-col items-center justify-between text-center overflow-hidden ${isSelf ? "ring-2 ring-emerald-400/60" : ""}`}
    >
      <div className={`font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.3em] ${p.ribbon}`}>
        {p.label} · #{rank}
      </div>
      <div className={`grid place-items-center w-10 h-10 sm:w-12 sm:h-12 rounded-full ${p.iconBg}`}>
        {p.icon}
      </div>
      <div className="min-w-0 w-full">
        <div className="font-display font-bold text-slate-100 text-sm sm:text-base truncate">{row.display_name}</div>
        <div className="mt-0.5 font-mono text-[10px] text-slate-400 truncate">{row.referral_code}</div>
      </div>
      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs">
        <span className="font-display font-extrabold text-emerald-300 text-base sm:text-xl leading-none">
          {row.referrals_converted}
        </span>
        <span className="font-mono text-slate-400">converted</span>
      </div>
      {isSelf && (
        <span className="absolute top-2 right-2 text-[9px] font-mono uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/40">
          You
        </span>
      )}
    </div>
  );
}

function TableRow({ row, rank, selfCode }) {
  const isSelf = row.referral_code === selfCode;
  const feeFreeWeeks = row.referrals_converted; // 1 converted = 7 fee-free days ≈ 1 week
  return (
    <div
      data-testid={`leaderboard-row-${rank}`}
      className={`grid grid-cols-[36px_1fr_auto_auto] sm:grid-cols-[48px_1fr_auto_auto_auto] gap-3 sm:gap-4 items-center px-3 sm:px-4 py-3 rounded-xl border transition ${
        isSelf
          ? "bg-emerald-500/10 border-emerald-400/40 shadow-[0_0_0_1px_rgba(16,185,129,0.25)]"
          : "bg-[#0F111A]/60 border-white/5 hover:border-emerald-400/20"
      }`}
    >
      <div className={`font-mono font-bold text-sm sm:text-base text-center ${rank <= 3 ? "text-amber-300" : "text-slate-400"}`}>
        #{rank}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-100 truncate">{row.display_name}</span>
          {isSelf && (
            <span className="text-[9px] font-mono uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/40">
              You
            </span>
          )}
        </div>
        <div className="mt-0.5 font-mono text-[10px] text-slate-500 truncate">{row.referral_code}</div>
      </div>
      <div className="text-right">
        <div className="font-display font-bold text-emerald-300">{row.referrals_converted}</div>
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">converted</div>
      </div>
      <div className="hidden sm:block text-right">
        <div className="font-mono text-sm text-slate-200">{row.referrals_joined}</div>
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">joined</div>
      </div>
      <div className="text-right">
        <div className="inline-flex items-center gap-1 font-display font-bold text-amber-300 text-sm">
          <Zap className="w-3.5 h-3.5" /> {feeFreeWeeks}
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">
          week{feeFreeWeeks === 1 ? "" : "s"}
        </div>
      </div>
    </div>
  );
}

function ShareBox({ status }) {
  const code = status?.referral_code;
  const shareUrl = code ? `${window.location.origin}/signup?ref=${encodeURIComponent(code)}` : "";

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copied — now paste it anywhere");
    } catch {
      toast.error("Copy failed");
    }
  };

  const share = async () => {
    if (!navigator.share) return copy(shareUrl);
    try {
      await navigator.share({
        title: "Join me on TradingoX",
        text: "Trade smarter with TradingoX — micro-dips in, micro-rips out, automatically.",
        url: shareUrl,
      });
    } catch (err) {
      // user cancelled or share unavailable — no action needed
      console.debug("[referral] share cancelled", err);
    }
  };

  if (!code) {
    return (
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 sm:p-6" data-testid="leaderboard-cta-guest">
        <div className="flex items-center gap-2 text-emerald-300 font-mono text-[10px] uppercase tracking-[0.3em]">
          <Sparkles className="w-3.5 h-3.5" /> compete · earn fee-free weeks
        </div>
        <div className="mt-2 font-display text-xl sm:text-2xl font-extrabold text-slate-100">
          Join TradingoX to get your referral ID
        </div>
        <p className="mt-2 text-sm text-slate-300 leading-relaxed">
          Every friend who closes their first profitable trade earns{" "}
          <b className="text-emerald-300">both of you 7 fee-free days</b> — refer enough and your platform fee could vanish for the month.
        </p>
        <Link
          to="/signup"
          data-testid="leaderboard-cta-signup"
          className="mt-4 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
        >
          Create my member account <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const weeksEarned = status.referrals_converted || 0;
  return (
    <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent p-5 sm:p-6" data-testid="leaderboard-cta-user">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-emerald-300 font-mono text-[10px] uppercase tracking-[0.3em]">
            <Trophy className="w-3.5 h-3.5" /> your referral station
          </div>
          <div className="mt-1 font-display text-lg sm:text-xl font-extrabold text-slate-100">
            {weeksEarned === 0 ? "Refer your first friend to board the leaderboard" : `You've earned ${weeksEarned} fee-free week${weeksEarned === 1 ? "" : "s"} so far`}
          </div>
        </div>
        <div className="flex items-center gap-2 text-amber-300 font-mono text-[11px] uppercase tracking-[0.2em]">
          <Flame className="w-4 h-4" /> {status.referrals_joined || 0} joined · {weeksEarned} converted
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-[auto_1fr_auto] gap-2 items-center bg-black/40 rounded-xl p-3 border border-white/5">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 sm:pr-2 sm:border-r sm:border-white/10">Your code</div>
        <div data-testid="leaderboard-my-code" className="font-mono font-bold text-emerald-300 truncate">
          {code}
        </div>
        <div className="flex items-center gap-2 justify-end">
          <Button
            data-testid="leaderboard-copy-code"
            onClick={() => copy(code)}
            variant="ghost"
            className="h-8 px-3 text-slate-300 hover:text-white hover:bg-white/10 text-xs"
          >
            <Copy className="w-3.5 h-3.5 mr-1" /> Code
          </Button>
          <Button
            data-testid="leaderboard-copy-link"
            onClick={() => copy(shareUrl)}
            variant="ghost"
            className="h-8 px-3 text-slate-300 hover:text-white hover:bg-white/10 text-xs"
          >
            <Copy className="w-3.5 h-3.5 mr-1" /> Link
          </Button>
          <Button
            data-testid="leaderboard-share"
            onClick={share}
            className="h-8 px-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-xs"
          >
            <Share2 className="w-3.5 h-3.5 mr-1" /> Share
          </Button>
        </div>
      </div>
      <div className="mt-2 font-mono text-[10px] text-slate-500 truncate" data-testid="leaderboard-my-link">
        {shareUrl}
      </div>
    </div>
  );
}

function MechanicsStrip() {
  const items = [
    { icon: Users, title: "1. Share your code", body: "Send your referral link to friends through text, X, Instagram, Discord." },
    { icon: Gift, title: "2. They sign up & profit", body: "They register with your code and close their first profitable trade." },
    { icon: Zap, title: "3. Both of you win", body: "You and your friend each unlock 7 fee-free trading days — stacking." },
  ];
  return (
    <div className="grid sm:grid-cols-3 gap-3 sm:gap-4" data-testid="leaderboard-mechanics">
      {items.map((it) => (
        <div key={it.title} className="rounded-2xl border border-white/10 bg-[#0F111A]/60 p-4">
          <div className="grid place-items-center w-9 h-9 rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30 mb-3">
            <it.icon className="w-4 h-4" />
          </div>
          <div className="font-display text-sm font-bold text-slate-100">{it.title}</div>
          <div className="mt-1 text-xs text-slate-400 leading-relaxed">{it.body}</div>
        </div>
      ))}
    </div>
  );
}

export default function ReferralLeaderboardPage() {
  const [board, setBoard] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let mounted = true;
    // Try authenticated status first; fall back silently to public leaderboard only
    referralsApi.status()
      .then((s) => { if (mounted) setStatus(s); })
      .catch(() => { if (mounted) setStatus(null); });
    referralsApi.leaderboard(BOARD_LIMIT)
      .then((b) => { if (mounted) setBoard(b); })
      .catch(() => {
        referralsApi.publicLeaderboard(BOARD_LIMIT)
          .then((b) => { if (mounted) setBoard(b); })
          .catch(() => { if (mounted) setBoard({ leaderboard: [] }); });
      });
    return () => { mounted = false; };
  }, []);

  const rows = board?.leaderboard || [];
  const selfCode = status?.referral_code;
  const myRank = useMemo(() => {
    if (!selfCode) return null;
    const i = rows.findIndex((r) => r.referral_code === selfCode);
    return i === -1 ? null : i + 1;
  }, [rows, selfCode]);

  const totalConverted = useMemo(() => rows.reduce((s, r) => s + (r.referrals_converted || 0), 0), [rows]);
  const totalJoined = useMemo(() => rows.reduce((s, r) => s + (r.referrals_joined || 0), 0), [rows]);

  return (
    <div className="min-h-screen bg-[#0B0C12] text-slate-100 relative overflow-x-hidden aq-grain">
      <div className="absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-amber-500/10 via-emerald-500/5 to-transparent pointer-events-none" />
      <header className="relative z-10 max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="leaderboard-logo-link">
          <img
            src="/brand/tradingox-logo.png"
            alt="TradingoX"
            className="w-10 h-10 rounded-full ring-1 ring-white/10 shadow-[0_0_20px_rgba(16,185,129,0.25)]"
          />
          <div className="font-display text-lg font-bold tracking-tight">TradingoX</div>
        </Link>
        <Link
          to={status ? "/dashboard" : "/"}
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-100"
          data-testid="leaderboard-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> {status ? "Back to dashboard" : "Back home"}
        </Link>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 pb-16">
        {/* Hero */}
        <section className="pt-4 pb-8 sm:pb-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300">
            <Trophy className="w-3.5 h-3.5" /> leaderboard · live
          </div>
          <h1 className="font-display mt-4 text-3xl sm:text-5xl font-extrabold tracking-tight">
            Top <span className="text-emerald-400">referrers</span> on TradingoX
          </h1>
          <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-slate-400 leading-relaxed">
            Every converted referral unlocks <b className="text-emerald-300">7 fee-free days</b> for you and your friend.
            Climb the ranks, waive your 1.25% platform fee, and let the bot bank clean profits — commission-free.
          </p>

          {/* KPIs */}
          <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-4 max-w-xl mx-auto" data-testid="leaderboard-kpis">
            <Kpi label="Members on board" value={rows.length} icon={Users} tone="text-slate-200" />
            <Kpi label="Total conversions" value={totalConverted} icon={Gift} tone="text-emerald-300" />
            <Kpi label="Fee-free weeks dealt" value={totalConverted} icon={Zap} tone="text-amber-300" />
          </div>
        </section>

        {/* Share / CTA box */}
        <ShareBox status={status} />

        {/* Mechanics */}
        <div className="mt-8">
          <MechanicsStrip />
        </div>

        {/* Podium */}
        <section className="mt-10">
          <div className="flex items-end justify-between gap-3 mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300">podium</div>
              <h2 className="font-display text-xl sm:text-2xl font-extrabold mt-1">The top 3</h2>
            </div>
            {myRank && myRank > 3 && (
              <div className="text-xs text-slate-400 font-mono">
                You're <b className="text-emerald-300">#{myRank}</b> — close the gap.
              </div>
            )}
          </div>
          {rows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/10 bg-[#0F111A]/50 p-10 text-center" data-testid="leaderboard-empty">
              <Trophy className="w-6 h-6 mx-auto text-amber-300" />
              <div className="mt-3 font-display font-bold text-slate-100">
                Nobody's referred a friend yet.
              </div>
              <div className="mt-1 text-xs text-slate-400 font-mono">
                Be the first — rank #1 is wide open.
              </div>
            </div>
          ) : (
            <Podium rows={rows} selfCode={selfCode} />
          )}
        </section>

        {/* Full ranks */}
        {rows.length > 3 && (
          <section className="mt-10">
            <div className="flex items-end justify-between gap-3 mb-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">rankings</div>
                <h2 className="font-display text-xl sm:text-2xl font-extrabold mt-1">Full standings</h2>
              </div>
              <div className="hidden sm:block text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
                converted · joined · weeks earned
              </div>
            </div>
            <div className="space-y-2" data-testid="leaderboard-table">
              {rows.slice(3).map((row, idx) => (
                <TableRow key={row.referral_code} row={row} rank={idx + 4} selfCode={selfCode} />
              ))}
            </div>
          </section>
        )}

        {/* Footer rallying strip */}
        <section className="mt-14 rounded-2xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent p-6 text-center">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-emerald-300">Keep climbing</div>
          <div className="mt-1 font-display text-xl sm:text-2xl font-bold">Totals here reset only when accounts churn — your weeks don't.</div>
          <p className="mt-2 text-sm text-slate-400 max-w-xl mx-auto">
            Rankings update in real time whenever a referred member closes their first profitable trade.
            Keep stacking conversions to lock in a longer fee-free runway.
          </p>
        </section>
      </main>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone = "text-slate-100" }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F111A]/60 p-3 sm:p-4">
      <div className="flex items-center justify-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`mt-1 font-display font-extrabold text-2xl sm:text-3xl ${tone}`}>{value}</div>
    </div>
  );
}
